import { z } from 'zod';

/**
 * ISO 3166-1 alpha-2 — every officially assigned code, as one list.
 *
 * This is a **code list, not content** (U16): Aysar ships the same build to any
 * market, so the platform must know every country without an operator seeding
 * one. Storing it as 250 `question_options` rows would make a fixed
 * international standard into editable data and hand staff the job of
 * translating the world; instead a `country` answer stores the code and the
 * clients render the name from their own i18n catalogs under `country.<CODE>`
 * (U8) — which they must ship in ar and en with full key parity.
 *
 * Deliberately **not** filtered to "countries we operate in": which markets a
 * brand serves is settings and product, and a member's country of residence or
 * origin may legitimately be anywhere.
 */
export const COUNTRY_CODES = [
  'AD',
  'AE',
  'AF',
  'AG',
  'AI',
  'AL',
  'AM',
  'AO',
  'AQ',
  'AR',
  'AS',
  'AT',
  'AU',
  'AW',
  'AX',
  'AZ',
  'BA',
  'BB',
  'BD',
  'BE',
  'BF',
  'BG',
  'BH',
  'BI',
  'BJ',
  'BL',
  'BM',
  'BN',
  'BO',
  'BQ',
  'BR',
  'BS',
  'BT',
  'BV',
  'BW',
  'BY',
  'BZ',
  'CA',
  'CC',
  'CD',
  'CF',
  'CG',
  'CH',
  'CI',
  'CK',
  'CL',
  'CM',
  'CN',
  'CO',
  'CR',
  'CU',
  'CV',
  'CW',
  'CX',
  'CY',
  'CZ',
  'DE',
  'DJ',
  'DK',
  'DM',
  'DO',
  'DZ',
  'EC',
  'EE',
  'EG',
  'EH',
  'ER',
  'ES',
  'ET',
  'FI',
  'FJ',
  'FK',
  'FM',
  'FO',
  'FR',
  'GA',
  'GB',
  'GD',
  'GE',
  'GF',
  'GG',
  'GH',
  'GI',
  'GL',
  'GM',
  'GN',
  'GP',
  'GQ',
  'GR',
  'GS',
  'GT',
  'GU',
  'GW',
  'GY',
  'HK',
  'HM',
  'HN',
  'HR',
  'HT',
  'HU',
  'ID',
  'IE',
  'IL',
  'IM',
  'IN',
  'IO',
  'IQ',
  'IR',
  'IS',
  'IT',
  'JE',
  'JM',
  'JO',
  'JP',
  'KE',
  'KG',
  'KH',
  'KI',
  'KM',
  'KN',
  'KP',
  'KR',
  'KW',
  'KY',
  'KZ',
  'LA',
  'LB',
  'LC',
  'LI',
  'LK',
  'LR',
  'LS',
  'LT',
  'LU',
  'LV',
  'LY',
  'MA',
  'MC',
  'MD',
  'ME',
  'MF',
  'MG',
  'MH',
  'MK',
  'ML',
  'MM',
  'MN',
  'MO',
  'MP',
  'MQ',
  'MR',
  'MS',
  'MT',
  'MU',
  'MV',
  'MW',
  'MX',
  'MY',
  'MZ',
  'NA',
  'NC',
  'NE',
  'NF',
  'NG',
  'NI',
  'NL',
  'NO',
  'NP',
  'NR',
  'NU',
  'NZ',
  'OM',
  'PA',
  'PE',
  'PF',
  'PG',
  'PH',
  'PK',
  'PL',
  'PM',
  'PN',
  'PR',
  'PS',
  'PT',
  'PW',
  'PY',
  'QA',
  'RE',
  'RO',
  'RS',
  'RU',
  'RW',
  'SA',
  'SB',
  'SC',
  'SD',
  'SE',
  'SG',
  'SH',
  'SI',
  'SJ',
  'SK',
  'SL',
  'SM',
  'SN',
  'SO',
  'SR',
  'SS',
  'ST',
  'SV',
  'SX',
  'SY',
  'SZ',
  'TC',
  'TD',
  'TF',
  'TG',
  'TH',
  'TJ',
  'TK',
  'TL',
  'TM',
  'TN',
  'TO',
  'TR',
  'TT',
  'TV',
  'TW',
  'TZ',
  'UA',
  'UG',
  'UM',
  'US',
  'UY',
  'UZ',
  'VA',
  'VC',
  'VE',
  'VG',
  'VI',
  'VN',
  'VU',
  'WF',
  'WS',
  'YE',
  'YT',
  'ZA',
  'ZM',
  'ZW',
] as const;

export const CountryCodeSchema = z.enum(COUNTRY_CODES);

export type CountryCode = (typeof COUNTRY_CODES)[number];

const CODE_SET: ReadonlySet<string> = new Set<string>(COUNTRY_CODES);

/**
 * Narrows a plain string. Case-**sensitive** on purpose: ISO alpha-2 is
 * upper-case, one canonical spelling per country is what makes a stored answer
 * comparable, and quietly accepting `sy` would put two spellings of Syria in the
 * same column.
 */
export function isCountryCode(value: string): value is CountryCode {
  return CODE_SET.has(value);
}

/** The i18n key a client renders a code with — the one place the convention lives. */
export function countryLabelKey(code: CountryCode): string {
  return `country.${code}`;
}

const REGIONAL_INDICATOR_BASE = 0x1f1e6 - 'A'.charCodeAt(0);

/** The flag emoji for a code — two regional-indicator symbols, no font or asset behind it. */
export function flagEmojiOf(code: CountryCode): string {
  return String.fromCodePoint(
    REGIONAL_INDICATOR_BASE + code.charCodeAt(0),
    REGIONAL_INDICATOR_BASE + code.charCodeAt(1),
  );
}
